import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  MessageSquare, Smartphone, Bot, Zap, Send, Users,
  Plus, Trash2, Edit, CheckCircle, TrendingUp, Target,
  DollarSign, MapPin, Truck, Settings, Activity
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const EQUIPMENT_TYPES = [
  'Sprinter Van', 'Cargo Van', 'Box Truck', 'Hotshot', 'Dry Van', 'Reefer', 
  'Flatbed', 'Step Deck', 'Conestoga', 'Lowboy', 'RGN', 'Double Drop', 
  'Curtain Side', 'Tanker', 'Hopper', 'Dump Truck', 'Power Only'
];

const AutomationHub = () => {
  const [activeTab, setActiveTab] = useState('sms');
  const [smsSubscribers, setSmsSubscribers] = useState([]);
  const [autobidRules, setAutobidRules] = useState([]);
  const [autobidStats, setAutobidStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // SMS form
  const [smsForm, setSmsForm] = useState({
    phone: '',
    driver_email: '',
    preferred_states: []
  });

  // Auto-bid form
  const [ruleForm, setRuleForm] = useState({
    driver_email: '',
    driver_name: '',
    driver_phone: '',
    rule_name: '',
    origin_states: [],
    destination_states: [],
    equipment_types: [],
    min_rate_per_mile: 2.0,
    max_miles: 1000,
    min_total_rate: 500,
    bid_percentage: 100,
    max_daily_bids: 10
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [subsRes, statsRes] = await Promise.all([
        axios.get(`${API}/sms/subscribers`),
        axios.get(`${API}/autobid/stats`)
      ]);
      setSmsSubscribers(subsRes.data.subscribers || []);
      setAutobidStats(statsRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSmsSubscribe = async () => {
    if (!smsForm.phone) {
      toast.error('Please enter a phone number');
      return;
    }
    
    try {
      await axios.post(`${API}/sms/subscribe`, smsForm);
      toast.success('SMS subscription created!');
      setSmsForm({ phone: '', driver_email: '', preferred_states: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    }
  };

  const handleCreateRule = async () => {
    if (!ruleForm.driver_email || !ruleForm.rule_name) {
      toast.error('Please enter driver email and rule name');
      return;
    }
    
    try {
      await axios.post(`${API}/autobid/rules`, ruleForm);
      toast.success(`Auto-bid rule "${ruleForm.rule_name}" created!`);
      setRuleForm({
        driver_email: '',
        driver_name: '',
        driver_phone: '',
        rule_name: '',
        origin_states: [],
        destination_states: [],
        equipment_types: [],
        min_rate_per_mile: 2.0,
        max_miles: 1000,
        min_total_rate: 500,
        bid_percentage: 100,
        max_daily_bids: 10
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create rule');
    }
  };

  const toggleState = (field, state) => {
    setRuleForm(prev => ({
      ...prev,
      [field]: prev[field].includes(state)
        ? prev[field].filter(s => s !== state)
        : [...prev[field], state]
    }));
  };

  const toggleEquipment = (eq) => {
    setRuleForm(prev => ({
      ...prev,
      equipment_types: prev.equipment_types.includes(eq)
        ? prev.equipment_types.filter(e => e !== eq)
        : [...prev.equipment_types, eq]
    }));
  };

  const tabs = [
    { id: 'sms', label: 'SMS Bot', icon: MessageSquare },
    { id: 'autobid', label: 'Auto-Bidding', icon: Bot }
  ];

  return (
    <Layout>
      <Toaster position="top-right" />
      <div className="p-6" data-testid="automation-hub-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Bot className="w-7 h-7 text-green-500" />
            Automation Hub
          </h1>
          <p className="text-gray-400 mt-1">SMS Load Bot & Auto-Bidding System</p>
        </div>

        {/* Stats Overview */}
        {autobidStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <Smartphone className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{smsSubscribers.length}</p>
                <p className="text-xs text-gray-400">SMS Subscribers</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <Bot className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{autobidStats.active_rules}</p>
                <p className="text-xs text-gray-400">Active Rules</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <Target className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{autobidStats.total_auto_bids}</p>
                <p className="text-xs text-gray-400">Auto-Bids Placed</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{autobidStats.win_rate}%</p>
                <p className="text-xs text-gray-400">Win Rate</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-green-600 text-white'
                  : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* SMS Bot Tab */}
        {activeTab === 'sms' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* SMS Setup */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-blue-500" />
                  SMS Load Bot Setup
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-blue-400 font-medium mb-2">How it works:</p>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li>• Drivers text your number to subscribe</li>
                    <li>• They receive matching loads via SMS</li>
                    <li>• They can bid by replying: BID &lt;id&gt; &lt;amount&gt;</li>
                    <li>• Commands: LOADS, BID, STOP, HELP</li>
                  </ul>
                </div>

                <div>
                  <Label className="text-gray-300">Driver Phone Number</Label>
                  <Input
                    value={smsForm.phone}
                    onChange={(e) => setSmsForm({...smsForm, phone: e.target.value})}
                    placeholder="+1 (555) 123-4567"
                    className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Driver Email (optional)</Label>
                  <Input
                    value={smsForm.driver_email}
                    onChange={(e) => setSmsForm({...smsForm, driver_email: e.target.value})}
                    placeholder="driver@email.com"
                    className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>

                <Button 
                  onClick={handleSmsSubscribe}
                  className="w-full bg-blue-600 hover:bg-blue-500"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add SMS Subscriber
                </Button>
              </CardContent>
            </Card>

            {/* SMS Subscribers List */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-500" />
                  SMS Subscribers ({smsSubscribers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {smsSubscribers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="w-10 h-10 mx-auto mb-3" />
                    <p>No SMS subscribers yet</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {smsSubscribers.map((sub, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{sub.phone}</p>
                          <p className="text-xs text-gray-500">
                            {sub.preferred_states?.length > 0 
                              ? sub.preferred_states.join(', ')
                              : 'All states'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 text-sm">{sub.loads_sent || 0} loads sent</p>
                          <p className={`text-xs ${sub.active ? 'text-green-500' : 'text-red-500'}`}>
                            {sub.active ? 'Active' : 'Inactive'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quo Setup Notice */}
            <Card className="bg-purple-500/10 border-purple-500/30 lg:col-span-2">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Settings className="w-5 h-5 text-purple-500 mt-0.5" />
                  <div>
                    <p className="text-purple-400 font-medium">Quo (OpenPhone) Setup</p>
                    <p className="text-sm text-gray-400 mt-1">
                      To enable SMS functionality with your Quo account:
                    </p>
                    <ol className="text-sm text-gray-400 mt-2 space-y-1 list-decimal list-inside">
                      <li>Get your API key from Quo Settings → API</li>
                      <li>Get your Phone Number ID from Quo</li>
                      <li>Set up webhook URL: <code className="text-purple-400">{window.location.origin}/api/sms/webhook/quo</code></li>
                    </ol>
                    <code className="block mt-2 p-2 bg-slate-900/50 rounded text-xs text-gray-300">
                      QUO_API_KEY=your_api_key<br/>
                      QUO_PHONE_NUMBER_ID=your_phone_id
                    </code>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Auto-Bidding Tab */}
        {activeTab === 'autobid' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Create Rule */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Bot className="w-5 h-5 text-green-500" />
                  Create Auto-Bid Rule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-green-400 font-medium mb-2">Auto-Bidding:</p>
                  <p className="text-sm text-gray-400">
                    Set rules and the system will automatically bid on matching loads for you.
                    While you sleep, you're winning loads!
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Driver Email *</Label>
                    <Input
                      value={ruleForm.driver_email}
                      onChange={(e) => setRuleForm({...ruleForm, driver_email: e.target.value})}
                      placeholder="driver@email.com"
                      className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Rule Name *</Label>
                    <Input
                      value={ruleForm.rule_name}
                      onChange={(e) => setRuleForm({...ruleForm, rule_name: e.target.value})}
                      placeholder="TX to CA Flatbeds"
                      className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                </div>

                {/* Origin States */}
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-500" />
                    Origin States (leave empty for all)
                  </Label>
                  <div className="flex flex-wrap gap-1 mt-2 p-2 bg-slate-700/30 rounded-lg max-h-24 overflow-y-auto">
                    {US_STATES.map(state => (
                      <button
                        key={state}
                        type="button"
                        onClick={() => toggleState('origin_states', state)}
                        className={`px-2 py-0.5 text-xs rounded transition-colors ${
                          ruleForm.origin_states.includes(state)
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-600/50 text-gray-400 hover:bg-slate-600'
                        }`}
                      >
                        {state}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Equipment Types */}
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Truck className="w-4 h-4 text-orange-500" />
                    Equipment Types
                  </Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {EQUIPMENT_TYPES.map(eq => (
                      <button
                        key={eq}
                        type="button"
                        onClick={() => toggleEquipment(eq)}
                        className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                          ruleForm.equipment_types.includes(eq)
                            ? 'bg-orange-500 text-white'
                            : 'bg-slate-600/50 text-gray-400 hover:bg-slate-600'
                        }`}
                      >
                        {eq}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Rate Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Min $/mile</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={ruleForm.min_rate_per_mile}
                      onChange={(e) => setRuleForm({...ruleForm, min_rate_per_mile: parseFloat(e.target.value)})}
                      className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Max Miles</Label>
                    <Input
                      type="number"
                      value={ruleForm.max_miles}
                      onChange={(e) => setRuleForm({...ruleForm, max_miles: parseInt(e.target.value)})}
                      className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Bid % of asking</Label>
                    <Input
                      type="number"
                      value={ruleForm.bid_percentage}
                      onChange={(e) => setRuleForm({...ruleForm, bid_percentage: parseInt(e.target.value)})}
                      className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">100 = full rate, 95 = 5% under</p>
                  </div>
                  <div>
                    <Label className="text-gray-300">Max daily bids</Label>
                    <Input
                      type="number"
                      value={ruleForm.max_daily_bids}
                      onChange={(e) => setRuleForm({...ruleForm, max_daily_bids: parseInt(e.target.value)})}
                      className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                </div>

                <Button 
                  onClick={handleCreateRule}
                  className="w-full bg-green-600 hover:bg-green-500"
                >
                  <Bot className="w-4 h-4 mr-2" /> Create Auto-Bid Rule
                </Button>
              </CardContent>
            </Card>

            {/* Active Rules & Stats */}
            <div className="space-y-6">
              {/* Top Auto-Bidders */}
              {autobidStats?.top_auto_bidders?.length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Activity className="w-5 h-5 text-purple-500" />
                      Top Auto-Bidders
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {autobidStats.top_auto_bidders.map((bidder, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                          <div>
                            <p className="text-white font-medium">{bidder.rule}</p>
                            <p className="text-xs text-gray-500">{bidder.driver}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-green-400 font-mono">{bidder.bids_placed}</p>
                            <p className="text-xs text-gray-500">bids placed</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* How It Works */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-500" />
                    How Auto-Bidding Works
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { step: '1', title: 'Set Your Rules', desc: 'Define lanes, equipment, and rate preferences' },
                    { step: '2', title: 'System Monitors', desc: 'New loads are checked against your rules 24/7' },
                    { step: '3', title: 'Auto-Bid Placed', desc: 'Matching loads get your bid automatically' },
                    { step: '4', title: 'You Get Notified', desc: 'Win a load? We\'ll let you know!' }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-400 text-xs font-bold">{item.step}</span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{item.title}</p>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AutomationHub;
